import { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, NUM, fmtDate, todayISO, sameOrNew } from "@/lib/format";
import { PageHeader, StatCard, Panel, Segmented, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { CalendarPlus, Trash2, CalendarClock, AlertCircle, CheckCircle2, Repeat, ChevronLeft, ChevronRight, FileText, Loader2, FileDown, Receipt, ShoppingCart, ScrollText, Calendar, Wallet, Sigma, BellRing } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const WD = [["Pzt", "Pazartesi"], ["Sal", "Salı"], ["Çar", "Çarşamba"], ["Per", "Perşembe"], ["Cum", "Cuma"], ["Cmt", "Cumartesi"], ["Paz", "Pazar"]];
const blank = { title: "", amount: 0, due_date: todayISO(), type: "fatura", recurring: false, paid: false, note: "" };

const TYPE_META = {
  teklif: { label: "Teklif Takibi", dot: "bg-blue-500", stripe: "border-l-blue-500", chip: "bg-blue-50 text-blue-800 border-blue-200", icon: FileText },
  alis: { label: "Alış Faturası Vadesi", dot: "bg-red-500", stripe: "border-l-red-500", chip: "bg-red-50 text-red-800 border-red-200", icon: ShoppingCart },
  satis: { label: "Satış Tahsilatı", dot: "bg-emerald-500", stripe: "border-l-emerald-500", chip: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Receipt },
  cek: { label: "Çek Vadesi", dot: "bg-purple-500", stripe: "border-l-purple-500", chip: "bg-purple-50 text-purple-800 border-purple-200", icon: ScrollText },
  sabit_odeme: { label: "Sabit Ödeme", dot: "bg-amber-500", stripe: "border-l-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200", icon: Repeat },
  ajanda: { label: "Mali Hatırlatıcı", dot: "bg-slate-500", stripe: "border-l-slate-400", chip: "bg-slate-100 text-slate-700 border-slate-200", icon: CalendarClock },
};
const metaOf = (t) => TYPE_META[t] || TYPE_META.ajanda;

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Agenda() {
  const [list, setList] = useState([]);
  const [events, setEvents] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(blank);
  const [view, setView] = useState("takvim");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [dayDlg, setDayDlg] = useState(null);
  const [pdfing, setPdfing] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const printRef = useRef(null);

  const load = () => { api.get("/agenda").then((r) => setList(sameOrNew(r.data))).catch(() => {}); };
  const loadEvents = () => api.get(`/agenda/calendar?year=${year}&month=${month + 1}`).then((r) => setEvents(sameOrNew(r.data.events || []))).catch(() => {});
  useAutoRefresh(() => { load(); loadEvents(); });
  useEffect(() => { load(); }, []);
  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [year, month]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Başlık gerekli");
    try {
      await api.post("/agenda", { ...form, amount: Number(form.amount) });
      toast.success("Kayıt eklendi"); setDlg(false); setForm(blank); load(); loadEvents();
    } catch (e) { toast.error(apiError(e)); }
  };
  const togglePaid = async (it) => { await api.put(`/agenda/${it.id}`, { paid: !it.paid }); load(); loadEvents(); };
  const del = async (id) => { await api.delete(`/agenda/${id}`); load(); loadEvents(); };
  const markEventPaid = async (e) => {
    try { await api.put(`/agenda/${e.ref_id}`, { paid: !e.paid }); toast.success(e.paid ? "Ödenmedi olarak işaretlendi" : "Ödendi olarak işaretlendi"); load(); await loadEvents(); setDayDlg(null); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delEvent = async (e) => {
    try { await api.delete(`/agenda/${e.ref_id}`); toast.success("Hatırlatıcı silindi"); load(); await loadEvents(); setDayDlg(null); }
    catch (err) { toast.error(apiError(err)); }
  };

  const today = todayISO();
  const isOverdue = (it) => !it.paid && (it.due_date || "").slice(0, 10) < today;
  const isLateEvent = (e) => e.date < today && ((e.source === "agenda" && !e.paid) || (e.source === "cheque" && !e.cleared));
  const upcoming = list.filter((i) => !i.paid).length;
  const totalDue = list.filter((i) => !i.paid).reduce((s, i) => s + i.amount, 0);
  const overdueCount = list.filter(isOverdue).length;
  const monthTotal = events.reduce((s, e) => s + (e.amount || 0), 0);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const cells = useMemo(() => monthGrid(year, month), [year, month]);
  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const typeSummary = useMemo(() => {
    const m = {};
    events.forEach((e) => { m[e.type] = m[e.type] || { count: 0, amount: 0 }; m[e.type].count++; m[e.type].amount += e.amount || 0; });
    return m;
  }, [events]);

  useEffect(() => {
    if (!pdfData) return;
    const t = setTimeout(async () => {
      try {
        const canvas = await html2canvas(printRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        const pdf = new jsPDF("p", "mm", "a4");
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const imgH = (canvas.height * pw) / canvas.width;
        const img = canvas.toDataURL("image/png");
        let heightLeft = imgH, pos = 0;
        pdf.addImage(img, "PNG", 0, pos, pw, imgH); heightLeft -= ph;
        while (heightLeft > 0) { pos -= ph; pdf.addPage(); pdf.addImage(img, "PNG", 0, pos, pw, imgH); heightLeft -= ph; }
        pdf.save(`ajanda-${year}-${String(month + 1).padStart(2, "0")}.pdf`);
        toast.success("Ajanda PDF indirildi");
      } catch { toast.error("PDF oluşturulamadı"); }
      finally { setPdfData(null); setPdfing(false); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [pdfData]);

  const exportPdf = () => { setPdfing(true); setPdfData({ events: [...events].sort((a, b) => a.date.localeCompare(b.date)) }); };
  const sortedEvents = useMemo(() => [...events].sort((a, b) => a.date.localeCompare(b.date)), [events]);

  const EventRow = ({ e, i, actions }) => {
    const M = metaOf(e.type); const late = isLateEvent(e);
    return (
      <div className={cn("relative flex items-center gap-3 pl-4 pr-3 py-2.5 border-l-4 rounded-r-lg bg-slate-50/60 border-y border-r border-slate-200/70 hover:bg-slate-100/80 transition-colors", M.stripe, late && "bg-red-50/60 border-red-200")} data-testid={`agenda-event-${i}`}>
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border", M.chip)}><M.icon className="h-4 w-4" /></div>
        <div className="w-24 shrink-0"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tarih</div><div className="text-sm font-semibold tnum">{fmtDate(e.date)}</div></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate flex items-center gap-2">{e.title}{late && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide"><AlertCircle className="h-3 w-3" />Gecikmede</span>}{e.paid && <span className="text-[10px] font-bold uppercase text-emerald-600">Ödendi</span>}</div>
          <div className="text-[11px] text-muted-foreground">{M.label}{e.ref_no ? <> · <span className="font-mono">{e.ref_no}</span></> : ""}{e.cleared ? " · Tahsil edildi" : ""}</div>
        </div>
        {e.amount ? <div className="text-right shrink-0"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tutar</div><div className="font-head font-black tnum text-sm">{TRY(e.amount)}</div></div> : null}
        {actions}
      </div>
    );
  };

  return (
    <div className="space-y-6 stagger-in" data-testid="agenda-page">
      <PageHeader title="Mali Ajanda & Ödeme Takvimi" subtitle="Fatura vadeleri, çek ödemeleri, teklif takibi ve mali hatırlatıcılar tek takvimde" badge="Vade Takibi">
        <Button variant="outline" onClick={goToday} data-testid="agenda-today-btn"><Calendar className="h-4 w-4 mr-2" />Bugüne Git</Button>
        <Button variant="outline" onClick={exportPdf} disabled={pdfing} data-testid="agenda-pdf-btn">
          {pdfing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Aylık PDF Raporu
        </Button>
        <Button onClick={() => { setForm({ ...blank, due_date: iso(year, month, Math.min(now.getDate(), 28)) }); setDlg(true); }} data-testid="add-agenda-btn"><CalendarPlus className="h-4 w-4 mr-2" />Yeni Etkinlik / Ödeme</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Bekleyen Hatırlatıcı" value={NUM(upcoming)} icon={BellRing} tone="warning" sub="ödenmemiş sabit ödeme & hatırlatıcı" testid="agenda-kpi-pending" />
        <StatCard label="Bekleyen Tutar" value={TRY(totalDue)} icon={Wallet} tone="primary" sub="tüm açık hatırlatıcılar" testid="agenda-kpi-pending-amount" />
        <StatCard label="Gecikmiş" value={NUM(overdueCount)} icon={AlertCircle} tone={overdueCount ? "danger" : "default"} sub={overdueCount ? "vadesi geçti · hemen ilgilen" : "gecikme yok"} testid="agenda-kpi-overdue" />
        <StatCard label={`${MONTHS[month]} Hareket Toplamı`} value={TRY(monthTotal)} icon={Sigma} tone="blue" sub={`${events.length} olay · teklif, fatura, çek`} testid="agenda-kpi-month-total" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 bg-card border rounded-xl p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={prevMonth} data-testid="agenda-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-head text-xl font-black min-w-[180px] text-center tracking-tight" data-testid="agenda-month-label">{MONTHS[month]} {year}</div>
          <Button size="icon" variant="outline" onClick={nextMonth} data-testid="agenda-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(typeSummary).map(([t, s]) => {
            const M = metaOf(t);
            return (
              <span key={t} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${M.chip}`} data-testid={`agenda-legend-${t}`}>
                <span className={`h-2 w-2 rounded-full ${M.dot}`} />{M.label} · {s.count}{s.amount ? <span className="tnum"> · {TRY(s.amount)}</span> : ""}
              </span>
            );
          })}
          {events.length === 0 && <span className="text-xs text-muted-foreground">Bu ay için otomatik olay yok.</span>}
        </div>
        <Segmented value={view} onChange={setView} testid="agenda-view" options={[["takvim", "Takvim"], ["liste", "Liste"]]} />
      </div>

      {view === "takvim" ? (
        <div className="bg-card border rounded-xl overflow-hidden shadow-sm" data-testid="agenda-calendar">
          <div className="grid grid-cols-7 border-b bg-slate-50">
            {WD.map(([s, l], i) => <div key={s} className={cn("px-2 py-2.5 text-center text-[11px] font-bold uppercase tracking-wider font-head", i >= 5 ? "text-primary/70" : "text-muted-foreground")}><span className="sm:hidden">{s}</span><span className="hidden sm:inline">{l}</span></div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, idx) => {
              if (!d) return <div key={idx} className="min-h-[112px] border-b border-r bg-slate-50/50" />;
              const dISO = iso(year, month, d);
              const dayEvents = byDay[dISO] || [];
              const isToday = dISO === today;
              const weekend = idx % 7 >= 5;
              const dayTotal = dayEvents.reduce((s, e) => s + (e.amount || 0), 0);
              const late = dayEvents.some(isLateEvent);
              return (
                <button key={idx} onClick={() => dayEvents.length && setDayDlg({ date: dISO, events: dayEvents })}
                  className={cn("relative min-h-[112px] border-b border-r p-1.5 text-left align-top transition-colors", weekend && "bg-slate-50/40", dayEvents.length ? "cursor-pointer hover:bg-orange-50/40" : "cursor-default", late && "ring-2 ring-inset ring-red-300 bg-red-50/40", isToday && "bg-primary/5")}
                  data-testid={`agenda-day-${dISO}`}>
                  <div className="flex items-start justify-between gap-1">
                    <div className={cn("text-xs font-bold h-6 w-6 flex items-center justify-center rounded-full font-head", isToday ? "bg-primary text-white shadow" : "text-slate-600")}>{d}</div>
                    {dayTotal > 0 && <div className="text-[10px] font-bold tnum text-slate-500 truncate max-w-[70%]" title={TRY(dayTotal)}>{TRY(dayTotal)}</div>}
                  </div>
                  {late && <span className="inline-flex mt-1 items-center gap-0.5 rounded-full bg-red-600 text-white px-1.5 py-px text-[9px] font-bold uppercase tracking-wide" data-testid={`agenda-late-${dISO}`}>Gecikmede</span>}
                  <div className="space-y-0.5 mt-1">
                    {dayEvents.slice(0, 3).map((e, i) => {
                      const M = metaOf(e.type);
                      return (
                        <div key={i} className={cn("truncate rounded px-1.5 py-0.5 text-[10px] font-medium border flex items-center gap-1", M.chip, isLateEvent(e) && "ring-1 ring-red-400")} title={`${M.label}: ${e.title}${e.amount ? " · " + TRY(e.amount) : ""}`}>
                          <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${M.dot}`} /><span className="truncate">{e.title}</span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div className="text-[10px] text-primary font-bold px-1">+{dayEvents.length - 3} daha</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <Panel title="Kronolojik Ajanda" subtitle={`${MONTHS[month]} ${year} · ${events.length} olay`} icon={CalendarClock} testid="agenda-list-view">
          {events.length === 0 ? <Empty title="Bu ay için kayıt yok" desc="Teklif, fatura, çek vadesi veya hatırlatıcı eklendiğinde burada görünür." /> : (
            <div className="space-y-1.5">{sortedEvents.map((e, i) => <EventRow key={i} e={e} i={i} />)}</div>
          )}
        </Panel>
      )}

      <Panel title="Sabit Ödemeler & Hatırlatıcılar" subtitle="Elle eklenen ödemeler · ödendi işaretle veya sil" icon={Repeat} testid="agenda-manual-panel"
        actions={<span className="text-xs font-bold tnum rounded-full bg-muted px-2 py-0.5" data-testid="agenda-manual-count">{list.length}</span>}>
        {list.length === 0 ? (
          <Empty title="Henüz sabit ödeme yok" desc="Kira, elektrik, vergi gibi tekrarlayan ödemeleri ekleyin." action={<Button size="sm" onClick={() => setDlg(true)} data-testid="agenda-empty-add-btn"><CalendarPlus className="h-4 w-4 mr-1.5" />Ekle</Button>} />
        ) : (
          <div className="space-y-2">
            {list.map((it) => {
              const late = isOverdue(it);
              return (
                <div key={it.id} className={cn("relative flex flex-col sm:flex-row sm:items-center gap-3 pl-4 pr-3 py-3 border-l-4 rounded-r-lg bg-slate-50/60 border-y border-r border-slate-200/70 hover:bg-slate-100/80 transition-colors", it.paid ? "border-l-emerald-500" : late ? "border-l-red-500 bg-red-50/50 border-red-200" : "border-l-amber-500")} data-testid={`agenda-${it.id}`}>
                  <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0 border", it.paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : late ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200")}>
                    {it.type === "sabit_odeme" ? <Repeat className="h-5 w-5" /> : <CalendarClock className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">{it.title}{it.recurring && <Badge variant="outline" className="text-[10px]">Aylık Tekrar</Badge>}{late && <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-1.5 py-px text-[10px] font-bold uppercase">Gecikmede</span>}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{it.type === "sabit_odeme" ? "Sabit Ödeme" : "Fatura Ödemesi"}{it.note && ` · ${it.note}`}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:gap-6 shrink-0">
                    <div><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Son Tarih</div><div className={cn("text-sm font-semibold tnum", late && "text-red-600")}>{fmtDate(it.due_date)}</div></div>
                    <div className="text-right"><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tutar</div><div className="font-head font-black tnum">{TRY(it.amount)}</div></div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant={it.paid ? "outline" : "default"} className={cn(!it.paid && "bg-emerald-600 hover:bg-emerald-700")} onClick={() => togglePaid(it)} data-testid={`agenda-pay-${it.id}`}>
                      {it.paid ? <><CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" />Ödendi</> : "Ödendi İşaretle"}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`agenda-del-${it.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <Dialog open={!!dayDlg} onOpenChange={(o) => !o && setDayDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="agenda-day-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">{dayDlg ? fmtDate(dayDlg.date) : ""} · Günün Mali Detayları</DialogTitle></DialogHeader>
          {dayDlg && (
            <div className="flex items-center justify-between rounded-lg bg-secondary text-white px-4 py-2.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 font-head">Günlük Toplam</span>
              <span className="font-head text-lg font-black tnum" data-testid="agenda-day-total">{TRY(dayDlg.events.reduce((s, e) => s + (e.amount || 0), 0))}</span>
            </div>
          )}
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {(dayDlg?.events || []).map((e, i) => (
              <EventRow key={i} e={e} i={`day-${i}`} actions={e.source === "agenda" && e.ref_id ? (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant={e.paid ? "outline" : "default"} className={cn("h-7 text-xs", !e.paid && "bg-emerald-600 hover:bg-emerald-700")} onClick={() => markEventPaid(e)} data-testid={`agenda-day-pay-${i}`}>{e.paid ? "Ödenmedi Yap" : "Ödendi"}</Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delEvent(e)} data-testid={`agenda-day-del-${i}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                </div>
              ) : null} />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" data-testid="agenda-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Yeni Etkinlik / Ödeme</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Başlık *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="a-title" placeholder="Kira, elektrik faturası, vergi..." /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Tutar (₺)"><Input type="number" className="tnum" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="a-amount" /></F>
              <F label="Son Ödeme Tarihi"><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} data-testid="a-date" /></F>
            </div>
            <F label="Tür">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="a-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fatura">Fatura Ödemesi</SelectItem>
                  <SelectItem value="sabit_odeme">Sabit Ödeme</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
              <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="a-recurring" />
              <Label className="text-sm">Her ay tekrarlansın</Label>
            </div>
            <F label="Not"><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="a-note" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} data-testid="a-cancel">İptal</Button>
            <Button onClick={save} data-testid="save-agenda">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pdfData && (
        <div style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", background: "#fff" }}>
          <div ref={printRef} style={{ padding: "40px", fontFamily: "'Segoe UI', Arial, sans-serif", color: "#1a1a1a", background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{ width: "6px", height: "44px", background: "#ea580c", borderRadius: "3px" }} />
                <div>
                  <div style={{ fontSize: "20px", fontWeight: 800 }}>GÜMÜŞALAN ELEKTRİK</div>
                  <div style={{ fontSize: "11px", color: "#999" }}>Aylık Mali Ajanda</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#ea580c" }}>AJANDA</div>
                <div style={{ fontSize: "13px", fontWeight: 700 }}>{MONTHS[month]} {year}</div>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#0b0f19", color: "#fff" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: "11px" }}>Tarih</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: "11px" }}>Tür</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: "11px" }}>Açıklama</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontSize: "11px" }}>Tutar</th>
                </tr>
              </thead>
              <tbody>
                {pdfData.events.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: "12px", fontSize: "12px", color: "#888" }}>Bu ay için kayıt yok.</td></tr>
                ) : pdfData.events.map((e, i) => (
                  <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                    <td style={{ padding: "7px 10px", fontSize: "11px", borderBottom: "1px solid #f0f0f0" }}>{fmtDate(e.date)}</td>
                    <td style={{ padding: "7px 10px", fontSize: "11px", borderBottom: "1px solid #f0f0f0" }}>{metaOf(e.type).label}</td>
                    <td style={{ padding: "7px 10px", fontSize: "11px", borderBottom: "1px solid #f0f0f0" }}>{e.title}</td>
                    <td style={{ padding: "7px 10px", fontSize: "11px", textAlign: "right", fontWeight: 600, borderBottom: "1px solid #f0f0f0" }}>{e.amount ? TRY(e.amount) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "24px", fontSize: "10px", color: "#bbb", textAlign: "center" }}>GÜMÜŞALAN ELEKTRİK yönetim sistemi · {NUM(pdfData.events.length)} kayıt</div>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
